import { ComponentProps } from "react"
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form"
import { Autocomplete, TextField } from "@mui/material"
import { z } from "zod";

type EnumBackedOption<ZEnum extends z.ZodEnum<any>> = {
  id: keyof z.infer<ZEnum>,
  label: string,
}

type EnumBackedAutocompleteProps<ZEnum extends z.ZodEnum<any> = z.ZodEnum<any>, TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = Omit<ComponentProps<typeof Autocomplete>, 'options' | 'renderInput'> & {
  control: Control<TFieldValues>;
  name: TName,
  label: ComponentProps<typeof TextField>['label'],
  options: EnumBackedOption<ZEnum>[],
}

const EnumBackedAutocomplete: React.FC<EnumBackedAutocompleteProps> = (props) => {
  const { control, name, label, options, ...autocompleteProps } = props

  const getOptionFrom = (value: string | null | undefined): typeof options[number] | string => {
    if (value == null) return ''
    return options.find(option => option.id === value) ?? ''
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        return (
          <Autocomplete
            {...autocompleteProps}
            ref={ref}
            options={options}
            value={getOptionFrom(value)}
            isOptionEqualToValue={(option, value) => {
              // We return true for the first option if the value is not set or is empty
              if ((value == null || value === '') && option === options[0]) {
                return value == null || value === ''
              }

              // If the value is a string and the option is an object, we match based on the option id
              if (typeof value === 'string' && typeof option === 'object' && option != null  && 'id' in option) {
                return option.id === value
              }

              // If the value is an object, we do an exact match
              return option === value
            }}
            onChange={(_, selectedOption: typeof options[number] | unknown) => {
              const updatedValue = (selectedOption as any)?.id ?? ''
              // TODO: We should probably do some more aggressive type checking here, to avoid runtime errors
              onChange(updatedValue)
            }}
            renderInput={(params) => <>
              <TextField
                {...params}
                label={label}
                error={!!error}
                helperText={error && error.message}
              />
              <input type="hidden" name={name} value={value} />
            </>}
          />
        )
      }}
    />
  )
}

export default EnumBackedAutocomplete
